import React, { useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useWeather } from '../../hooks/useWeather';
import { formatTempC } from '../../services/meteoApi';
import { MeteoConditionIcon } from './MeteoConditionIcon';
import WeatherDialog from './WeatherDialog';

interface WeatherChipProps {
  /** Compact for headers next to titles / datetime */
  size?: 'sm' | 'md';
  enabled?: boolean;
  /** Extra sx for the clickable row */
  sx?: object;
}

/**
 * Chip compacto: ícono + temperatura (1 decimal + °C). Tap → dialog detalle.
 */
const WeatherChip: React.FC<WeatherChipProps> = ({
  size = 'sm',
  enabled = true,
  sx,
}) => {
  const { t, i18n } = useTranslation();
  const { data } = useWeather({ enabled });
  const [open, setOpen] = useState(false);

  const configured = Boolean(data?.configured && data?.current);
  const iconSize = size === 'sm' ? 18 : 20;
  const fontSize = size === 'sm' ? '0.7rem' : '0.8rem';

  if (!enabled) return null;

  // Sin ubicación: no ocupar espacio en cabeceros (solo admin configura en Settings)
  if (data && !data.configured) return null;
  if (!data || !configured) return null;

  const temp = formatTempC(data.current?.temperature_c, i18n.language || 'es');
  const label = t(`meteo.conditions.${data.current?.label_key || 'unknown'}`, {
    defaultValue: '',
  });

  return (
    <>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(true)}
        aria-label={label ? `${label}, ${temp}` : temp}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          m: 0,
          p: 0.25,
          border: 'none',
          background: 'transparent',
          cursor: 'pointer',
          color: '#fff',
          borderRadius: 1,
          flexShrink: 0,
          lineHeight: 1,
          '&:hover': { backgroundColor: 'rgba(35,145,255,0.12)' },
          ...sx,
        }}
      >
        <MeteoConditionIcon
          icon={data.current?.icon}
          isDay={data.current?.is_day !== false}
          sx={{
            fontSize: iconSize,
            ...(data.stale ? { color: '#aaa' } : {}),
          }}
        />
        <Typography
          component="span"
          sx={{
            fontSize,
            fontWeight: 600,
            color: data.stale ? '#bbb' : '#fff',
            fontVariantNumeric: 'tabular-nums',
            whiteSpace: 'nowrap',
            letterSpacing: '-0.01em',
          }}
        >
          {temp}
        </Typography>
      </Box>
      <WeatherDialog open={open} onClose={() => setOpen(false)} data={data} />
    </>
  );
};

export default WeatherChip;
